/* eslint-disable @typescript-eslint/no-unnecessary-condition */

/* eslint-disable unicorn/no-useless-undefined */
import type { JWTPayload as BasicJWTPayload } from "jose";
import { flare } from "@flareapp/js";
import axios from "axios";
import { decodeJwt } from "jose";
import { login as loginCall, refresh as refreshCall } from "~/lib/api/auth";
import { md5 } from "~/lib/hashing";

export type JWTPayload = BasicJWTPayload & {
    app_id: number;
    features: Record<string, boolean>;
    is_admin: boolean;
    is_owner: boolean;
    login_url: string;
    permissions: string[];
    prv: string;
    user_id: number;
};

axios.defaults.headers.common = {
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
};
const oneMinute = 60 * 1000;

export const useTokenStore = defineStore("tokenStore", () => {
    const route = useRoute();
    const { slug } = storeToRefs(useSlugStore());
    const timer = ref<ReturnType<typeof setTimeout>>();
    const token = useSlugLocalStorage<string | undefined>(
        "tokenStore-token",
        undefined,
    );

    const refreshToken = useSlugLocalStorage<string | undefined>(
        "tokenStore-refreshToken",
        undefined,
    );

    const refreshTokenTtl = useSlugLocalStorage<string | undefined>(
        "tokenStore-refreshTokenTtl",
        undefined,
    );

    const tokenLogoutHash = useSlugLocalStorage<string | undefined>(
        "tokenStore-tokenLogoutHash",
        undefined,
    );

    const now = useNow({ interval: oneMinute });

    const isRefreshTokenExpired = computed<boolean>(() => {
        if (refreshTokenTtl.value === undefined) {
            return true;
        }

        return (
            new Date(refreshTokenTtl.value).getTime() - now.value.getTime() < 0
        );
    });

    const isTokenExpired = computed<boolean>(() => {
        if (payload.value?.exp === undefined) {
            return true;
        }

        return payload.value.exp * 1000 - now.value.getTime() - oneMinute < 0;
    });

    const isLoggedIn = computed<boolean>(
        () =>
            token.value !== undefined &&
            (!isTokenExpired.value || !isRefreshTokenExpired.value),
    );

    // eslint-disable-next-line unicorn/consistent-function-scoping
    function isRedirectParameterValid(redirectPath: string) {
        const redirectPathSplit = redirectPath.split("/").filter(Boolean);

        if (redirectPathSplit.length <= 1) {
            return false;
        }

        if (
            redirectPathSplit.length >= 2 &&
            redirectPathSplit[1].toLowerCase() === "login"
        ) {
            return false;
        }

        return true;
    }

    const { locale } = storeToRefs(useLocalSettingsStore());

    const config = useRuntimeConfig();
    const baseUrl = computed(
        () => `${config.public.merchantApiBaseUrl}/${slug.value ?? ""}`,
    );

    watchEffect(() => {
        axios.defaults.baseURL = baseUrl.value;
    });

    const payload = computed(() => {
        if (!token.value) return;

        try {
            return decodeJwt<JWTPayload>(token.value);
        } catch {
            return;
        }
    });

    // Sync changes to the token or refreshToken in real-time
    watch([token, refreshToken, refreshTokenTtl], ([newToken]) => {
        if (!newToken) {
            void navigateToLogin();
        }
    });

    watchEffect(() => {
        if (isTokenExpired.value && !isRefreshTokenExpired.value) {
            if (
                tokenLogoutHash.value &&
                md5(token.value ?? "") === tokenLogoutHash.value
            ) {
                void logout();
            } else {
                void refresh();
            }
        }
    });

    if (!slug.value) {
        void navigateToLogin();
    }

    async function navigateToLogin() {
        if (!slug.value) {
            return showError({
                statusCode: 404,
            });
        }

        const fullPath = route.fullPath;

        if (fullPath === `/${slug.value}/dashboard`) {
            return await navigateTo({
                name: "slug-login",
                params: { slug: slug.value },
            });
        }

        return await navigateTo({
            name: "slug-login",
            params: { slug: slug.value },

            query: { redirect: fullPath },
        });
    }

    async function logout() {
        if (!isLoggedIn.value && !slug.value) {
            return;
        }

        if (token.value) {
            tokenLogoutHash.value = md5(token.value);
        }

        setToken();

        await navigateToLogin();
    }

    function startRefreshTokenTimer() {
        if (timer.value) clearTimeout(timer.value);

        if (!payload.value?.exp) {
            throw new Error('"exp" not set in JWT!');
        }

        const timeout =
            payload.value?.exp * 1000 - now.value.getTime() - oneMinute;
        timer.value = setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
            refresh().catch((error) => {
                console.log(error);
            });
        }, timeout);
    }

    function setToken(tokenData?: {
        locale?: string;
        refreshToken?: string;
        refreshTokenTtl?: string;
        token?: string;
    }) {
        refreshToken.value = tokenData?.refreshToken;
        refreshTokenTtl.value = tokenData?.refreshTokenTtl;
        token.value = tokenData?.token;
        axios.defaults.headers.common.Authorization = `Bearer ${token.value ?? ""}`;

        if (tokenData?.locale) {
            locale.value = tokenData.locale;
            axios.defaults.headers.common["Accept-Language"] = tokenData.locale;
        }

        if (token.value && payload.value?.exp) startRefreshTokenTimer();
        else if (timer.value) clearTimeout(timer.value);
    }

    function refreshIfNecessary() {
        if (!isTokenExpired.value) {
            return;
        }

        return refresh();
    }

    async function refresh() {
        if (refreshToken.value) {
            if (
                tokenLogoutHash.value &&
                md5(token.value ?? "") === tokenLogoutHash.value
            ) {
                await navigateToLogin();

                return;
            }

            try {
                const refreshResponse = await refreshCall(refreshToken.value);
                setToken({
                    refreshToken: refreshResponse.refresh_token,
                    refreshTokenTtl: refreshResponse.refresh_token_ttl,
                    token: refreshResponse.access_token,
                });

                return;
            } catch {
                await navigateToLogin();
            }
        }

        await navigateToLogin();
    }

    async function login(
        email: string,
        password: string,
        _slug: string,
        intendedRoute?: string,
    ) {
        const loginData = await loginCall({ email, password });

        if (
            loginData.access_token &&
            loginData.refresh_token &&
            loginData.refresh_token_ttl
        ) {
            setToken({
                refreshToken: loginData.refresh_token,
                refreshTokenTtl: loginData.refresh_token_ttl,
                token: loginData.access_token,
            });
            tokenLogoutHash.value = undefined;

            await attemptNavigateToRedirect(intendedRoute);
        } else {
            await flare.report(
                new Error("Login was correct but no Token was received"),
            );
        }
    }

    async function attemptNavigateToRedirect(intendedRoute?: string) {
        const decodedRedirect = decodeURIComponent(
            intendedRoute ?? (route.query.redirect as string) ?? "",
        );

        if (
            decodedRedirect &&
            slug.value &&
            decodedRedirect.startsWith(`/${slug.value}`)
        ) {
            const [path, queryString] = decodedRedirect.split("?");
            const query = queryString
                ? Object.fromEntries(new URLSearchParams(queryString))
                : undefined;

            if (!isRedirectParameterValid(decodedRedirect)) {
                return await navigateToDashboard();
            }

            try {
                return await navigateTo({
                    path,
                    query,
                });
            } catch {
                return await navigateToDashboard();
            }
        }

        return await navigateToDashboard();
    }

    async function navigateToDashboard() {
        if (!slug.value) {
            return showError({ statusCode: 404 });
        }

        return await navigateTo({
            name: "slug-dashboard",
            params: { slug: slug.value },
        });
    }

    setToken({
        locale: locale.value,
        refreshToken: refreshToken.value,
        refreshTokenTtl: refreshTokenTtl.value,
        token: token.value,
    });

    return {
        attemptNavigateToRedirect,
        baseUrl,
        isLoggedIn,
        login,
        logout,
        navigateToLogin,
        payload: readonly(payload),
        refresh,
        refreshIfNecessary,
        refreshToken: readonly(refreshToken),
        setToken,
        token: readonly(token),
    };
});
